import React, { useState, useEffect } from 'react';
import { 
    ChevronLeft, 
    ChevronRight, 
    ZoomIn, 
    ZoomOut, 
    Maximize, 
    Download, 
    X,
    Minimize
} from 'lucide-react';

function FlipbookViewer({ marca, onClose }) {
    const [currentPage, setCurrentPage] = useState(marca.paginaInicio - 1);
    const [isFlipping, setIsFlipping] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [bookPosition, setBookPosition] = useState({ x: 0, y: 0 });

    // Array de páginas del catálogo
    // IMPORTANTE: Aquí van las rutas de las imágenes del PDF convertido
    const catalogPages = Array.from(
        { length: marca.totalPaginas }, 
        (_, i) => `${marca.catalogoPath}/page-${i + 1}.jpg`
    );

    const goToNextPage = () => {
        if (currentPage < marca.totalPaginas - 2 && !isFlipping) {
            setIsFlipping(true);
            setTimeout(() => {
                setCurrentPage(prev => Math.min(prev + 2, marca.totalPaginas - 2));
                setIsFlipping(false);
            }, 600);
        }
    };

    const goToPrevPage = () => {
        if (currentPage > 0 && !isFlipping) {
            setIsFlipping(true);
            setTimeout(() => {
                setCurrentPage(prev => Math.max(prev - 2, 0));
                setIsFlipping(false);
            }, 600);
        }
    };

    const goToPage = (pageNum) => {
        if (!isFlipping) {
            setIsFlipping(true);
            setTimeout(() => {
                setCurrentPage(Math.min(Math.max(pageNum, 0), marca.totalPaginas - 2));
                setIsFlipping(false);
            }, 600);
        }
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 0.25, 4));
    };

    const handleZoomOut = () => {
        setZoom(prev => {
            const newZoom = Math.max(prev - 0.25, 0.5);
            // Si reducimos el zoom a 100% o menos, resetear posición
            if (newZoom <= 1) {
                setBookPosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    };
     // Funciones para arrastrar cuando hay zoom
    const handleMouseDown = (e) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - bookPosition.x,
                y: e.clientY - bookPosition.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && zoom > 1) {
            const newX = e.clientX - dragStart.x;
            const newY = e.clientY - dragStart.y;
            setBookPosition({ x: newX, y: newY });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    // Zoom con rueda del ratón (centrado en cursor)
    const handleWheel = (e) => {
        e.preventDefault();
        
        const delta = e.deltaY > 0 ? -0.1 : 0.1; // -0.1 para zoom out, +0.1 para zoom in
        const newZoom = Math.min(Math.max(zoom + delta, 0.5), 4); // Límites 0.5 a 4
        
        // Si reducimos el zoom a 100% o menos, resetear posición
        if (newZoom <= 1) {
            setZoom(newZoom);
            setBookPosition({ x: 0, y: 0 });
        } else {
            // Calcular el punto central para hacer zoom hacia donde está el cursor
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            // Ajustar la posición para que el zoom se centre en el cursor
            const zoomRatio = newZoom / zoom;
            const newX = bookPosition.x - (x - rect.width / 2) * (zoomRatio - 1) / newZoom;
            const newY = bookPosition.y - (y - rect.height / 2) * (zoomRatio - 1) / newZoom;
            
            setZoom(newZoom);
            setBookPosition({ x: newX, y: newY });
        }
    };

    // Touch events para móvil
    const handleTouchStart = (e) => {
        if (zoom > 1 && e.touches.length === 1) {
            setIsDragging(true);
            setDragStart({
                x: e.touches[0].clientX - bookPosition.x,
                y: e.touches[0].clientY - bookPosition.y
            });
        }
    };

    const handleTouchMove = (e) => {
        if (isDragging && zoom > 1 && e.touches.length === 1) {
            const newX = e.touches[0].clientX - dragStart.x;
            const newY = e.touches[0].clientY - dragStart.y;
            setBookPosition({ x: newX, y: newY });
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    const toggleFullscreen = () => {
        const elem = document.documentElement;
        
        if (!document.fullscreenElement) {
            // Entrar a fullscreen
            if (elem.requestFullscreen) {
                elem.requestFullscreen();
            } else if (elem.webkitRequestFullscreen) { /* Safari */
                elem.webkitRequestFullscreen();
            } else if (elem.msRequestFullscreen) { /* IE11 */
                elem.msRequestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            // Salir de fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) { /* Safari */
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) { /* IE11 */
                document.msExitFullscreen();
            }
            setIsFullscreen(false);
        }
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = marca.pdfUrl;
        link.download = `Catalogo-${marca.nombre}.pdf`;
        link.click();
    };

    // Navegación con teclado
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === 'ArrowRight' && currentPage < marca.totalPaginas - 2 && !isFlipping) {
                setIsFlipping(true);
                setTimeout(() => {
                    setCurrentPage(prev => Math.min(prev + 2, marca.totalPaginas - 2));
                    setIsFlipping(false);
                }, 600);
            }
            if (e.key === 'ArrowLeft' && currentPage > 0 && !isFlipping) {
                setIsFlipping(true);
                setTimeout(() => {
                    setCurrentPage(prev => Math.max(prev - 2, 0));
                    setIsFlipping(false);
                }, 600);
            }
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [currentPage, isFlipping, marca.totalPaginas, onClose]);

    // Detectar cambios de fullscreen
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    return (
        <div className={`flipbook-viewer-modal ${isFullscreen ? 'fullscreen-mode' : ''}`}>
            {/* Header del catálogo */}
            <div className="flipbook-modal-header" style={{ background: marca.color }}>
                <div className="header-left-modal">
                    <div className="marca-logo-header">
                        <img src={marca.logo} alt={marca.nombre} />
                    </div>
                    <div className="marca-info-header">
                        <h3>{marca.nombre}</h3>
                        <span className="page-counter-modal">
                            págs. {currentPage + 1} - {currentPage + 2} de {marca.totalPaginas}
                        </span>
                    </div>
                </div>

                <div className="header-right-modal">
                    <button 
                        className="header-btn-modal"
                        onClick={toggleFullscreen}
                        title={isFullscreen ? "Salir pantalla completa" : "Pantalla completa"}
                    >
                        {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
                    </button>
                    <button 
                        className="header-btn-modal"
                        onClick={handleDownload}
                        title="Descargar catálogo"
                    >
                        <Download size={20} /> 
                    </button>
                    <button 
                        className="header-btn-modal header-btn-close"
                        onClick={onClose}
                        title="Cerrar"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Área del libro */}
            <div 
                className="flipbook-viewer-area"
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onWheel={handleWheel}
            >
                {/* Botón navegación izquierda */}
                <button 
                    className="nav-arrow-modal nav-arrow-left-modal"
                    onClick={goToPrevPage}
                    disabled={currentPage === 0 || isFlipping}
                    style={{ 
                        borderColor: marca.color,
                        color: marca.color 
                    }}
                >
                    <ChevronLeft size={32} />
                </button>

                {/* El libro con efecto 3D */}
                <div 
                    className={`book-container-modal ${zoom > 1 ? 'draggable' : ''} ${isDragging ? 'dragging' : ''}`}
                    style={{ 
                        transform: `scale(${zoom}) translate(${bookPosition.x / zoom}px, ${bookPosition.y / zoom}px)`,
                        cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                        transition: isDragging ? 'none' : 'transform 0.3s ease'
                    }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                >
                    <div className={`book-modal ${isFlipping ? 'flipping' : ''}`}>
                        {/* Página izquierda */}
                        <div className="page-modal page-left-modal">
                            <div className="page-content-modal">
                                <div className="page-number-modal">{currentPage + 1}</div>
                                
                                {/* Placeholder o imagen real */}
                                <div className="page-image-wrapper">
                                    {/* OPCIÓN 1: Con imágenes reales del PDF */}
                                    <img 
                                        src={catalogPages[currentPage]} 
                                        alt={`Página ${currentPage + 1}`}
                                        className="page-image-modal"
                                    />

                                    {/* OPCIÓN 2: Placeholder temporal */}
                                    
                                </div>

                                <div className="page-shadow-modal page-shadow-left-modal"></div>
                            </div>
                        </div>

                        {/* Página derecha */}
                        <div className="page-modal page-right-modal">
                            <div className="page-content-modal">
                                <div className="page-number-modal">{currentPage + 2}</div>
                                
                                <div className="page-image-wrapper">
                                    {/* OPCIÓN 1: Con imágenes reales */}
                                    <img 
                                        src={catalogPages[currentPage + 1]} 
                                        alt={`Página ${currentPage + 2}`}
                                        className="page-image-modal"
                                    />

                                    {/* OPCIÓN 2: Placeholder temporal */}
                                    
                                </div>

                                <div className="page-shadow-modal page-shadow-right-modal"></div>
                            </div>
                        </div>

                        {/* Lomo del libro */}
                        <div className="book-spine-modal" style={{ background: marca.color }}></div>
                    </div>
                </div>

                {/* Botón navegación derecha */}
                <button 
                    className="nav-arrow-modal nav-arrow-right-modal"
                    onClick={goToNextPage}
                    disabled={currentPage >= marca.totalPaginas - 2 || isFlipping}
                    style={{ 
                        borderColor: marca.color,
                        color: marca.color 
                    }}
                >
                    <ChevronRight size={32} />
                </button>
            </div>

            {/* Toolbar inferior */}
            <div className="flipbook-modal-toolbar" style={{ borderTopColor: marca.color }}>
                <div className="toolbar-section-modal">
                    {/* Navegación por páginas */}
                    <button 
                        className="toolbar-btn-modal" 
                        onClick={goToPrevPage} 
                        disabled={currentPage === 0}
                        style={{ color: marca.color }}
                    >
                        <ChevronLeft size={20} />
                    </button>
                    
                    <div className="page-input-group-modal">
                        <input 
                            type="number"
                            min="1"
                            max={marca.totalPaginas}
                            value={currentPage + 1}
                            onChange={(e) => goToPage(parseInt(e.target.value) - 1)}
                            className="page-input-modal"
                            style={{ borderColor: marca.color }}
                        />
                        <span className="page-separator-modal">/</span>
                        <span className="total-pages-modal">{marca.totalPaginas}</span>
                    </div>

                    <button 
                        className="toolbar-btn-modal" 
                        onClick={goToNextPage} 
                        disabled={currentPage >= marca.totalPaginas - 2}
                        style={{ color: marca.color }}
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>

                <div className="toolbar-section-modal">
                    {/* Controles de zoom */}
                    <button 
                        className="toolbar-btn-modal" 
                        onClick={handleZoomOut} 
                        disabled={zoom <= 0.5}
                        style={{ color: marca.color }}
                    >
                        <ZoomOut size={20} />
                    </button>
                    <span className="zoom-indicator-modal" style={{ color: marca.color }}>
                        {Math.round(zoom * 100)}%
                    </span>
                    <button 
                        className="toolbar-btn-modal" 
                        onClick={handleZoomIn} 
                        disabled={zoom >= 4}
                        style={{ color: marca.color }}
                    >
                        <ZoomIn size={20} />
                    </button>
                </div>
            </div>

            {/* Instrucción de teclado */}
            <div className="flipbook-hint">
                <p>
                    💡 Usa las flechas del teclado o ESC para cerrar
                    {zoom > 1 && " • Arrastra para mover"}
                </p>
            </div>
        </div>
    );
}

export default FlipbookViewer;